// ============================================================================
// SHARED GRADING LOGIC
//
// This is the single source of truth for "is this answer correct" and
// "how many points does it earn". Both Userform.js (when a candidate
// submits) and Thankyou.js (when showing results) import from here.
//
// WHY THIS FILE EXISTS
// ---------------------
// Previously, scoring at submission time and correctness-highlighting at
// display time were two separate hand-written implementations. They could
// silently disagree — in particular, a radio/text question's `answerKey`
// is supposed to be a *string*, but a newly created question in the builder
// defaulted `answerKey` to `[]` (an array) until the admin explicitly
// clicked the correct-answer radio next to an option. If that click was
// missed, `answerKey` stayed `[]`, and every comparison like
// `userResponse === question.answerKey` ("OptionA" === []) was always
// false — so a candidate's correct selection was scored 0 and rendered as
// wrong, with no error anywhere to indicate why.
//
// This module fixes that class of bug by:
//   1. Normalizing whitespace on every comparison (trim()).
//   2. Coercing whatever shape `answerKey` happens to be into the shape the
//      question type actually needs, instead of failing a strict === check.
//   3. Being the ONLY place this logic lives, so submission scoring and
//      results-page highlighting are guaranteed to agree.
// ============================================================================

const norm = (v) => (v === undefined || v === null ? "" : String(v).trim());

export function getQuestionPoints(question) {
  const configuredPoints = Number(question?.points);
  return configuredPoints > 0 ? configuredPoints : 1;
}

/**
 * Returns the set of correct option texts for a question, regardless of
 * whether `answerKey` was stored as a string, an array, or is malformed/
 * missing. This is what makes grading resilient to the shape-mismatch bug
 * described above.
 */
export function getCorrectOptionTexts(question) {
  const key = question?.answerKey;

  if (Array.isArray(key)) {
    return key.map(norm).filter(Boolean);
  }

  const single = norm(key);
  return single ? [single] : [];
}

/** True if `optionText` is one of the question's correct answers. */
export function isOptionCorrect(question, optionText) {
  return getCorrectOptionTexts(question).includes(norm(optionText));
}

/** True if the candidate selected this particular option. */
export function isOptionSelected(userResponseValue, optionText) {
  const target = norm(optionText);
  if (!target) return false;

  if (Array.isArray(userResponseValue)) {
    return userResponseValue.map(norm).includes(target);
  }
  return norm(userResponseValue) === target;
}

/**
 * Grades a single question against a single response value.
 * `userResponseValue` is a string for radio/text, an array for checkbox.
 */
export function gradeQuestion(question, userResponseValue) {
  const points = getQuestionPoints(question);
  const type = question?.questionType;
  const correctSet = getCorrectOptionTexts(question);

  if (type === "checkbox") {
    if (correctSet.length === 0) return 0;
    const selected = Array.isArray(userResponseValue)
      ? userResponseValue.map(norm).filter(Boolean)
      : [];
    const correctSelected = selected.filter((a) => correctSet.includes(a)).length;
    const incorrectSelected = selected.length - correctSelected;
    const ratio =
      correctSelected / correctSet.length - incorrectSelected / correctSet.length;
    return Math.max(0, ratio * points);
  }

  if (type === "radio") {
    const answer = norm(userResponseValue);
    return answer && correctSet.includes(answer) ? points : 0;
  }

  if (type === "text") {
    const expected = correctSet[0] || "";
    const answer = norm(userResponseValue).toLowerCase();
    return expected && answer === expected.toLowerCase() ? points : 0;
  }

  return 0;
}

/**
 * Grades a full set of raw response values (as collected live in
 * Userform.js's `responses` state array) against a question list.
 * Returns { totalPoints, scoreDetails } ready to store on the response doc.
 */
export function gradeSubmission(questions, rawResponses) {
  let totalPoints = 0;
  const scoreDetails = [];

  (questions || []).forEach((question, i) => {
    const value = rawResponses?.[i] ?? "";
    const pts = gradeQuestion(question, value);
    totalPoints += pts;
    scoreDetails.push({ question: question?.questionText, points: pts });
  });

  return { totalPoints, scoreDetails };
}

/**
 * Re-grades an already-submitted response doc for display on the results
 * page. Takes the frozen question snapshot (`ques`) and the stored
 * `responses` array (each entry shaped { question, answer, answerKey }).
 * Deliberately ignores the per-response `answerKey` field and regrades
 * straight from the frozen question's own `answerKey` — one less place
 * for the two numbers to drift apart.
 */
export function gradeStoredResponse(questions, storedResponses) {
  const rawResponses = (questions || []).map(
    (_, i) => storedResponses?.[i]?.answer ?? ""
  );
  return gradeSubmission(questions, rawResponses);
}

/** Does this question have a usable correct-answer configured at all? */
export function questionHasAnswerKey(question) {
  if (question?.questionType === "text") {
    return norm(question?.answerKey).length > 0;
  }
  return getCorrectOptionTexts(question).length > 0;
}
